import {
  endOfMonth,
  format,
  isValid,
  parse,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns"
import { enUS, es, ptBR } from "date-fns/locale"
import { CalendarDays } from "lucide-react"
import { useMemo, useState } from "react"
import type { DateRange } from "react-day-picker"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

import AppCalendar from "./app-calendar"

/** Intervalo fechado — a tela sempre trabalha com as duas pontas definidas. */
export interface AppDateRange {
  from: Date
  to: Date
}

interface DateRangePreset {
  key: string
  resolve: (reference: Date) => AppDateRange
}

/**
 * Atalhos da coluna "Mais usados". Ficam fora do componente porque não dependem
 * de estado: cada um recebe a data de referência e devolve o intervalo — o que
 * também os torna testáveis sem congelar o relógio.
 */
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    key: "today",
    resolve: (today) => ({ from: today, to: today }),
  },
  {
    key: "yesterday",
    resolve: (today) => ({
      from: subDays(today, 1),
      to: subDays(today, 1),
    }),
  },
  {
    key: "last7Days",
    resolve: (today) => ({
      from: subDays(today, 6),
      to: today,
    }),
  },
  {
    key: "last14Days",
    resolve: (today) => ({
      from: subDays(today, 13),
      to: today,
    }),
  },
  {
    key: "last30Days",
    resolve: (today) => ({
      from: subDays(today, 29),
      to: today,
    }),
  },
  {
    key: "last90Days",
    resolve: (today) => ({
      from: subDays(today, 89),
      to: today,
    }),
  },
  {
    key: "thisMonth",
    resolve: (today) => ({
      from: startOfMonth(today),
      to: today,
    }),
  },
  {
    key: "lastMonth",
    resolve: (today) => {
      const previous = subMonths(today, 1)
      return {
        from: startOfMonth(previous),
        to: endOfMonth(previous),
      }
    },
  },
]

const QUICK_UNITS = ["days", "weeks", "months"] as const

type QuickUnit = (typeof QUICK_UNITS)[number]

const QUICK_UNIT_IN_DAYS: Record<QuickUnit, number> = {
  days: 1,
  weeks: 7,
  months: 30,
}

const DEFAULT_QUICK_AMOUNT = "7"

const INPUT_DATE_MASK = "dd/MM/yyyy"

/**
 * Ajustes do calendário no modo INTERVALO.
 *
 * O `AppCalendar` dimensiona a célula com `w-full` e a linha é `flex` — numa seleção de dia único
 * isso não aparece, mas num intervalo cada semana vira uma faixa esticada de ponta a ponta. Fixar
 * a célula em 32px deixa a faixa contínua e do tamanho dos dias, com arredondamento só nas pontas
 * REAIS do intervalo, e não no fim de cada linha.
 */
const CALENDAR_RANGE_CLASSNAMES = {
  months: "flex flex-col gap-4 sm:flex-row",
  month: "space-y-3",
  row: "mt-1 flex w-full",
  head_cell:
    "w-8 rounded-md text-[0.8rem] font-normal text-muted-foreground",
  cell: "relative h-8 w-8 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has(>.day-range-start)]:rounded-l-md [&:has(>.day-range-end)]:rounded-r-md",
  day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100",
}

/** Locale do date-fns por idioma do i18n — nomes de mês e dias do calendário. */
const DATE_FNS_LOCALES = {
  "pt-BR": ptBR,
  en: enUS,
  es,
} as const

const isSameDay = (first: Date, second: Date) =>
  format(first, "yyyy-MM-dd") ===
  format(second, "yyyy-MM-dd")

const isSameRange = (
  first: AppDateRange,
  second: AppDateRange
) =>
  isSameDay(first.from, second.from) &&
  isSameDay(first.to, second.to)

const parseInputDate = (value: string) => {
  const parsed = parse(value, INPUT_DATE_MASK, new Date())
  return isValid(parsed) ? parsed : undefined
}

interface AppDateRangePickerProps {
  value: AppDateRange
  onChange: (range: AppDateRange) => void
  /**
   * Id do gatilho. Sem ele, o `<Label htmlFor>` de quem usa o componente não aponta para
   * controle nenhum: o leitor de tela anuncia só "Intervalo de datas" e perde o nome do
   * campo ("Período", "Data do pedido") que a tela dá a ele.
   */
  id?: string
  /** Última data selecionável — o padrão do portal é não olhar para o futuro. */
  maxDate?: Date
  align?: "start" | "center" | "end"
  disabled?: boolean
  className?: string
}

/**
 * Seletor de intervalo de datas do portal: atalhos ("últimos N dias"), lista de
 * períodos mais usados e calendário de intervalo, tudo num popover só.
 *
 * O intervalo só sobe para o formulário quando o operador confirma (clique num
 * atalho ou "Aplicar intervalo"): arrastar o calendário não dispara consulta a
 * cada clique.
 */
export const AppDateRangePicker = ({
  value,
  onChange,
  id,
  maxDate = new Date(),
  align = "start",
  disabled = false,
  className,
}: AppDateRangePickerProps) => {
  const { t, i18n } = useTranslation()
  const calendarLocale =
    DATE_FNS_LOCALES[
      i18n.language as keyof typeof DATE_FNS_LOCALES
    ] ?? ptBR
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState<DateRange>({
    from: value.from,
    to: value.to,
  })
  const [quickAmount, setQuickAmount] = useState(
    DEFAULT_QUICK_AMOUNT
  )
  const [quickUnit, setQuickUnit] =
    useState<QuickUnit>("days")
  const [fromText, setFromText] = useState(
    format(value.from, INPUT_DATE_MASK)
  )
  const [toText, setToText] = useState(
    format(value.to, INPUT_DATE_MASK)
  )

  const activePresetKey = useMemo(() => {
    const matched = DATE_RANGE_PRESETS.find((preset) =>
      isSameRange(preset.resolve(maxDate), value)
    )
    return matched?.key
  }, [maxDate, value])

  const triggerLabel = activePresetKey
    ? t(`common.dateRange.presets.${activePresetKey}`)
    : `${format(value.from, INPUT_DATE_MASK)} – ${format(value.to, INPUT_DATE_MASK)}`

  const applyRange = (range: AppDateRange) => {
    setDraft({ from: range.from, to: range.to })
    setFromText(format(range.from, INPUT_DATE_MASK))
    setToText(format(range.to, INPUT_DATE_MASK))
    onChange(range)
    setIsOpen(false)
  }

  const handleQuickApply = () => {
    const amount = Number(quickAmount)
    if (!Number.isFinite(amount) || amount <= 0) return
    const days = amount * QUICK_UNIT_IN_DAYS[quickUnit]
    applyRange({
      from: subDays(maxDate, days - 1),
      to: maxDate,
    })
  }

  const handleDraftChange = (range?: DateRange) => {
    setDraft(range ?? { from: undefined, to: undefined })
    if (range?.from)
      setFromText(format(range.from, INPUT_DATE_MASK))
    if (range?.to)
      setToText(format(range.to, INPUT_DATE_MASK))
  }

  const handleTextApply = () => {
    const from = parseInputDate(fromText)
    const to = parseInputDate(toText)
    if (!from || !to || from > to) return
    applyRange({ from, to })
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) return
    // Reabrir sempre parte do valor vigente: rascunho abandonado não sobrevive.
    setDraft({ from: value.from, to: value.to })
    setFromText(format(value.from, INPUT_DATE_MASK))
    setToText(format(value.to, INPUT_DATE_MASK))
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          variant="outline"
          disabled={disabled}
          aria-label={t("common.dateRange.label")}
          className={cn(
            "h-10 w-full justify-start gap-2 font-normal",
            className
          )}
        >
          <CalendarDays
            size={16}
            className="shrink-0 text-muted-foreground"
          />
          <span className="truncate">{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-auto max-w-[95vw] p-0"
      >
        <div className="flex flex-col sm:flex-row">
          <div className="w-full border-b p-4 sm:w-64 sm:border-b-0 sm:border-r">
            <p className="text-sm font-semibold">
              {t("common.dateRange.quickTitle")}
            </p>
            <div className="mt-3 flex items-end gap-2">
              <Input
                type="number"
                min={1}
                value={quickAmount}
                onChange={(event) =>
                  setQuickAmount(event.target.value)
                }
                aria-label={t(
                  "common.dateRange.quickAmount"
                )}
                className="h-9 w-16"
              />
              <Select
                value={quickUnit}
                onValueChange={(unit) =>
                  setQuickUnit(unit as QuickUnit)
                }
              >
                <SelectTrigger
                  className="h-9"
                  aria-label={t(
                    "common.dateRange.quickUnit"
                  )}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUICK_UNITS.map((unit) => (
                    <SelectItem key={unit} value={unit}>
                      {t(`common.dateRange.units.${unit}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              onClick={handleQuickApply}
              className="btn-whitelabel mt-3 w-full"
            >
              {t("common.dateRange.apply")}
            </Button>

            <p className="mt-5 text-sm font-semibold">
              {t("common.dateRange.mostUsedTitle")}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1">
              {DATE_RANGE_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() =>
                    applyRange(preset.resolve(maxDate))
                  }
                  aria-pressed={
                    activePresetKey === preset.key
                  }
                  className={cn(
                    "whitespace-nowrap rounded px-1 py-1 text-left text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                    activePresetKey === preset.key &&
                      "text-whitelabel font-medium"
                  )}
                >
                  {t(
                    `common.dateRange.presets.${preset.key}`
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="p-4">
            <p className="text-sm font-semibold">
              {t("common.dateRange.rangeTitle")}
            </p>
            <AppCalendar
              mode="range"
              selected={draft}
              onSelect={handleDraftChange}
              defaultMonth={value.from}
              // Dois meses: um recorte de mês cruza a virada, e com um mês só metade da
              // seleção fica fora de vista.
              numberOfMonths={2}
              // Sem os dias do mês vizinho: lado a lado eles apareceriam DUAS vezes, os dois
              // destacados, e não dá para saber em qual grade clicar.
              showOutsideDays={false}
              classNames={CALENDAR_RANGE_CLASSNAMES}
              locale={calendarLocale}
              toDate={maxDate}
              className="p-0 pt-2"
            />
            <div className="mt-3 grid grid-cols-2 gap-2">
              <label className="text-xs text-muted-foreground">
                {t("common.startDate")}
                <Input
                  value={fromText}
                  onChange={(event) =>
                    setFromText(event.target.value)
                  }
                  placeholder={INPUT_DATE_MASK}
                  className="mt-1 h-9"
                />
              </label>
              <label className="text-xs text-muted-foreground">
                {t("common.endDate")}
                <Input
                  value={toText}
                  onChange={(event) =>
                    setToText(event.target.value)
                  }
                  placeholder={INPUT_DATE_MASK}
                  className="mt-1 h-9"
                />
              </label>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleTextApply}
              className="mt-3 w-full"
            >
              {t("common.dateRange.applyRange")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default AppDateRangePicker
